import React, { useState, useCallback, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Checkbox,
  Radio,
  TextField,
  RadioGroup,
  FormControlLabel,
  Chip,
  CircularProgress,
  Fade,
  Slider,
  IconButton,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import { NavigateBefore, NavigateNext, Send } from "@mui/icons-material";
import { useIntl } from "react-intl";
import api from "../../api";
import { parseJWTToken } from "../../helpers";
import VoiceTranscription from "./VoiceTranscription";
import SurveyCompletion from "./SurveyCompletion";
import { backgroundDimming, questionCardBackground, rgbaToCss, surveyBackgroundStyle } from "./surveyStyle";

// Constants for question types
const QUESTION_TYPES = {
  SINGLE_CHOICE: "SINGLE_CHOICE",
  MULTI_CHOICE: "MULTI_CHOICE",
  TEXT: "TEXT",
  OPEN_TEXT: "OPEN_TEXT",
  LIKERT_SCALE: "LIKERT_SCALE",
};

// Utility function to strip HTML
const stripHtml = (html) => {
  if (!html) return "";
  const div = document.createElement("div");
  div.innerHTML = html;
  return div.textContent || div.innerText || "";
};

// CSS styles to support React Quill formatting in preview mode
const quillPreviewStyles = `
  /* Ensure inline styles from React Quill are properly displayed */
  .quill-content strong {
    font-weight: bold !important;
  }
  
  .quill-content em {
    font-style: italic !important;
  }
  
  .quill-content u {
    text-decoration: underline !important;
  }
  
  /* Support for React Quill's inline styles */
  .quill-content [style*="font-size"] {
    /* Font size is handled via inline styles */
  }
  
  .quill-content [style*="color"] {
    /* Color is handled via inline styles */
  }
  
  /* Preserve all formatting from React Quill */
  .quill-content * {
    font-family: inherit !important;
  }
  
  .quill-content p {
    margin: 0;
    padding: 0;
  }

  /* Quill alignment classes */
  .quill-content .ql-align-center {
    text-align: center !important;
  }

  .quill-content .ql-align-right {
    text-align: right !important;
  }

  .quill-content .ql-align-justify {
    text-align: justify !important;
  }

  .quill-content .ql-align-left {
    text-align: left !important;
  }
`;

// Main SurveyPreview component
function SurveyPreview({
  steps = [],
  settings = {},
  mode = "preview", // "preview" or "respond"
  onSubmit,
  onClose,
  token, // Add token prop for tracking
  surveyId, // Required for save API in respond mode
}) {
  const intl = useIntl();
  const [currentPage, setCurrentPage] = useState(0);
  const [answers, setAnswers] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [hasTrackedLinkOpen, setHasTrackedLinkOpen] = useState(false);
  const [validationErrors, setValidationErrors] = useState(new Set());
  const scrollContainerRef = useRef(null);

  // Inject CSS styles for React Quill formatting support
  useEffect(() => {
    const styleSheet = document.createElement("style");
    styleSheet.type = "text/css";
    styleSheet.innerText = quillPreviewStyles;
    document.head.appendChild(styleSheet);

    return () => {
      document.head.removeChild(styleSheet);
    };
  }, []);

  // Group questions into pages based on Page Breaks
  const pages = React.useMemo(() => {
    const pageGroups = [];
    let currentPageQuestions = [];

    steps.forEach((step) => {
      if (step.type === "PAGE_BREAK") {
        if (currentPageQuestions.length > 0) {
          pageGroups.push(currentPageQuestions);
          currentPageQuestions = [];
        }
      } else {
        currentPageQuestions.push(step);
      }
    });

    // Add the last page if there are remaining questions
    if (currentPageQuestions.length > 0) {
      pageGroups.push(currentPageQuestions);
    }

    // If there are no pages, add an empty page to avoid rendering issues
    if (pageGroups.length === 0) {
      pageGroups.push([]);
    }

    return pageGroups;
  }, [steps]);

  // Required questions: exclude TEXT and PAGE_BREAK, include only is_required/isMandatory
  const requiredSteps = React.useMemo(() => {
    return steps
      .filter(s => s.type !== "TEXT" && s.type !== "PAGE_BREAK" && (s.is_required || s.isMandatory))
      .map(s => ({ id: s.id, type: s.type?.toUpperCase() }));
  }, [steps]);

  const totalPages = pages.length;

  // Handle answer changes
  const handleAnswerChange = useCallback((questionId, value) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: value,
    }));
    // Clear validation error for this question when user answers
    setValidationErrors(prev => {
      const next = new Set(prev);
      next.delete(questionId);
      return next;
    });

    // Track LINK survey open on first question interaction
    if (mode === "respond" && token && !hasTrackedLinkOpen) {
      const trackLinkSurveyOpen = async () => {
        try {
          const tokenData = parseJWTToken(token);
          if (tokenData && tokenData.type === 'public_link' && tokenData.collector_id) {
            console.log('Tracking LINK survey open for collector:', tokenData.collector_id);
            await api.trackSurveyOpen(tokenData.collector_id, token);
            setHasTrackedLinkOpen(true);
          }
        } catch (error) {
          console.error('Failed to track survey open:', error);
          // Don't throw error as this shouldn't break the survey experience
        }
      };

      trackLinkSurveyOpen();
    }
  }, [mode, token, hasTrackedLinkOpen]);

  const scrollToTop = useCallback(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTo({ top: 0, behavior: "smooth" });
    }
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  // Returns true if the given page index contains at least one non-TEXT question
  const pageHasQuestions = useCallback((pageIndex) => {
    const pageSteps = pages[pageIndex] || [];
    return pageSteps.some(s => s.type?.toUpperCase() !== "TEXT");
  }, [pages]);

  // Builds the answers payload for POST /surveys/{id}/save/{token}/ from a given page
  const buildSavePayload = useCallback((pageIndex) => {
    const pageSteps = pages[pageIndex] || [];
    const payload = [];

    pageSteps.forEach((step) => {
      const qid = step.id;
      const stepType = step.type?.toUpperCase();
      const value = answers[qid];

      if (stepType === "TEXT" || value === undefined || value === null || value === "") return;

      if (stepType === QUESTION_TYPES.SINGLE_CHOICE || stepType === QUESTION_TYPES.LIKERT_SCALE) {
        const matchingChoice = step.choices?.find(c => (c.value || c.text) === value);
        const choiceId = matchingChoice?.id ?? matchingChoice?.value;
        if (choiceId !== undefined) {
          payload.push({
            question_id: qid,
            question_type: stepType === QUESTION_TYPES.LIKERT_SCALE ? "likert_scale" : "single_choice",
            choice_id: choiceId,
          });
        }
      } else if (stepType === QUESTION_TYPES.MULTI_CHOICE) {
        if (Array.isArray(value) && value.length > 0) {
          const choiceIds = value
            .map(v => {
              const match = step.choices?.find(c => (c.value || c.text) === v);
              return match?.id ?? match?.value;
            })
            .filter(id => id !== undefined);
          if (choiceIds.length > 0) {
            payload.push({ question_id: qid, question_type: "multi_choice", choice_ids: choiceIds });
          }
        }
      } else if (stepType === QUESTION_TYPES.OPEN_TEXT) {
        if (typeof value === "string" && value.trim()) {
          payload.push({ question_id: qid, question_type: "open_text", text: value });
        }
      }
    });

    return payload;
  }, [pages, answers]);

  // Calls save API for the given page if in respond mode and the page has questions.
  // When isSubmit is true the request is always sent (even with an empty answers array)
  // so the backend can mark the respondent as completed.
  const savePageAnswers = useCallback(async (pageIndex, isSubmit = false) => {
    if (mode !== "respond" || !surveyId || !token) return;
    const payload = pageHasQuestions(pageIndex) ? buildSavePayload(pageIndex) : [];
    if (!isSubmit && payload.length === 0) return;
    await api.saveSurveyResponse(surveyId, token, payload, isSubmit);
  }, [mode, surveyId, token, pageHasQuestions, buildSavePayload]);

  // Returns IDs of unanswered required questions for a given set of steps
  const getMissingIds = useCallback((stepsToCheck) => {
    const missing = [];
    stepsToCheck.forEach(({ id, type }) => {
      const value = answers[id];
      let isValid = false;
      if (type === QUESTION_TYPES.MULTI_CHOICE) {
        isValid = Array.isArray(value) && value.length > 0;
      } else if (type === QUESTION_TYPES.OPEN_TEXT) {
        isValid = typeof value === "string" && value.trim().length > 0;
      } else {
        isValid = value != null && value !== "";
      }
      if (!isValid) missing.push(id);
    });
    return missing;
  }, [answers]);

  // Validate all required questions (used on submit)
  const getMissingRequiredIds = useCallback(() => {
    return getMissingIds(requiredSteps);
  }, [getMissingIds, requiredSteps]);

  // Validate only the required questions on a specific page (used on next)
  const getMissingRequiredIdsOnPage = useCallback((pageIndex) => {
    const pageStepIds = new Set((pages[pageIndex] || []).map(s => s.id));
    return getMissingIds(requiredSteps.filter(s => pageStepIds.has(s.id)));
  }, [getMissingIds, pages, requiredSteps]);

  // Handle navigation
  const handlePrevious = useCallback(async () => {
    if (currentPage <= 0) return;
    if (mode === "respond") {
      setIsSaving(true);
      try {
        await savePageAnswers(currentPage);
      } catch (error) {
        console.error("Failed to save answers on back:", error);
      } finally {
        setIsSaving(false);
      }
    }
    setCurrentPage(currentPage - 1);
    scrollToTop();
  }, [currentPage, mode, savePageAnswers, scrollToTop]);

  const handleNext = useCallback(async () => {
    if (currentPage >= totalPages - 1) return;
    if (mode === "respond") {
      const missingOnPage = getMissingRequiredIdsOnPage(currentPage);
      if (missingOnPage.length > 0) {
        setValidationErrors(new Set(missingOnPage));
        return;
      }
      setIsSaving(true);
      try {
        await savePageAnswers(currentPage);
      } catch (error) {
        console.error("Failed to save answers on next:", error);
      } finally {
        setIsSaving(false);
      }
    }
    setCurrentPage(currentPage + 1);
    scrollToTop();
  }, [currentPage, totalPages, mode, getMissingRequiredIdsOnPage, savePageAnswers, scrollToTop]);

  // Step 1: validate then open the confirmation dialog
  const handleSubmit = useCallback(() => {
    if (mode !== "respond") return;

    const missingIds = getMissingRequiredIds();
    if (missingIds.length > 0) {
      setValidationErrors(new Set(missingIds));
      const firstMissingId = missingIds[0];
      const pageIndex = pages.findIndex(page => page.some(s => s.id === firstMissingId));
      if (pageIndex >= 0 && pageIndex !== currentPage) {
        setCurrentPage(pageIndex);
      }
      return;
    }

    setConfirmOpen(true);
  }, [mode, getMissingRequiredIds, pages, currentPage]);

  // Step 2: user confirmed — save and show success page
  const handleConfirmSubmit = useCallback(async () => {
    setConfirmOpen(false);
    setIsSubmitting(true);
    setValidationErrors(new Set());
    try {
      await savePageAnswers(currentPage, true);
      setIsSubmitted(true);
    } catch (error) {
      console.error("Failed to save answers on submit:", error);
    } finally {
      setIsSubmitting(false);
    }
  }, [currentPage, savePageAnswers]);

  // Get background style based on settings
  const getBackgroundStyle = useCallback(() => surveyBackgroundStyle(settings), [settings]);

  // Render individual question
  const renderQuestion = useCallback((step, index) => {
    const qid = step.id;
    const stepType = step.type?.toUpperCase();

    // Calculate question number
    const questionsBeforeCurrentPage = pages
      .slice(0, currentPage)
      .reduce((sum, page) => sum + page.filter(s => s.type !== "TEXT").length, 0);
    const nonTextQuestionsOnCurrentPage = pages[currentPage]
      .slice(0, index)
      .filter(s => s.type !== "TEXT").length;
    const questionNumber = stepType !== "TEXT" ? questionsBeforeCurrentPage + nonTextQuestionsOnCurrentPage + 1 : null;

    const rawQuestionText = step.question && step.question !== "<p><br></p>"
      ? step.question
      : `${stepType === "TEXT" ? "Content" : stepType?.replace("_", " ")} (Please add text)`;

    const isInteractive = mode === "respond";
    const isRequired = !!(step.is_required || step.isMandatory);
    const hasError = validationErrors.has(qid);

    switch (stepType) {
      case QUESTION_TYPES.SINGLE_CHOICE:
        return (
          <QuestionCard
            key={qid}
            questionNumber={questionNumber}
            questionText={rawQuestionText}
            description={step.description}
            settings={settings}
            isRequired={isRequired}
            hasError={hasError}
          >
            {step.choices && step.choices.length > 0 ? (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                {step.choices
                  .sort((a, b) => (a.index || 0) - (b.index || 0)) // Sort by index for consistent ordering
                  .map((choice) => (
                    <ChoiceOption
                      key={choice.value || choice.text}
                      choice={choice}
                      isSelected={answers[qid] === (choice.value || choice.text)}
                      onSelect={() => isInteractive && handleAnswerChange(qid, choice.value || choice.text)}
                      isInteractive={isInteractive}
                      settings={settings}
                    />
                  ))}
              </Box>
            ) : (
              <Typography sx={{ color: rgbaToCss(settings.fontColour), fontStyle: "italic", textAlign: "center", py: 2 }}>
                No choices available for this question.
              </Typography>
            )}
          </QuestionCard>
        );

      case QUESTION_TYPES.MULTI_CHOICE:
        const selectedChoices = new Set(answers[qid] || []);
        return (
          <QuestionCard
            key={qid}
            questionNumber={questionNumber}
            questionText={rawQuestionText}
            description={step.description}
            settings={settings}
            isRequired={isRequired}
            hasError={hasError}
          >
            {step.choices && step.choices.length > 0 ? (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                {step.choices
                  .sort((a, b) => (a.index || 0) - (b.index || 0)) // Sort by index for consistent ordering
                  .map((choice) => {
                    const choiceValue = choice.value || choice.text;
                    return (
                      <ChoiceOption
                        key={choiceValue}
                        choice={choice}
                        isSelected={selectedChoices.has(choiceValue)}
                        onSelect={() => {
                          if (!isInteractive) return;
                          const newSelected = new Set(selectedChoices);
                          if (newSelected.has(choiceValue)) {
                            newSelected.delete(choiceValue);
                          } else {
                            newSelected.add(choiceValue);
                          }
                          handleAnswerChange(qid, Array.from(newSelected));
                        }}
                        isInteractive={isInteractive}
                        settings={settings}
                        isMultiChoice
                      />
                    );
                  })}
              </Box>
            ) : (
              <Typography sx={{ color: rgbaToCss(settings.fontColour), fontStyle: "italic", textAlign: "center", py: 2 }}>
                No choices available for this question.
              </Typography>
            )}
          </QuestionCard>
        );

      case QUESTION_TYPES.TEXT:
        return (
          <ContentCard
            key={qid}
            questionNumber={questionNumber}
            contentText={rawQuestionText}
            description={step.description}
            settings={settings}
          />
        );

      case QUESTION_TYPES.OPEN_TEXT:
        return (
          <QuestionCard
            key={qid}
            questionNumber={questionNumber}
            questionText={rawQuestionText}
            description={step.description}
            settings={settings}
            isRequired={isRequired}
            hasError={hasError}
          >
            <TextField
              multiline
              minRows={4}
              maxRows={8}
              fullWidth
              disabled={!isInteractive}
              value={answers[qid] || ""}
              onChange={(e) => isInteractive && handleAnswerChange(qid, e.target.value)}
              placeholder="Enter your response here..."
              variant="outlined"
              sx={{
                '& .MuiOutlinedInput-root': {
                  color: rgbaToCss(settings.fontColour),
                  borderRadius: "8px",
                  backgroundColor: isInteractive ? 'rgba(0, 0, 0, 0.02)' : 'transparent',
                  '& fieldset': {
                    borderColor: '#e0e0e0',
                    borderWidth: '2px',
                  },
                  '&:hover fieldset': {
                    borderColor: isInteractive ? rgbaToCss(settings.fontColour) : '#e0e0e0',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: rgbaToCss(settings.fontColour),
                  },
                },
                '& .MuiInputBase-input': {
                  resize: 'vertical',
                  '&::placeholder': {
                    color: rgbaToCss(settings.fontColour),
                    opacity: 0.6,
                  }
                },
              }}
            />
            {isInteractive && (
              <VoiceTranscription
                settings={settings}
                surveyId={surveyId}
                token={token}
                onTranscript={(text) => {
                  const current = answers[qid] || "";
                  const merged = current.trim() ? `${current.trim()} ${text}` : text;
                  handleAnswerChange(qid, merged);
                }}
              />
            )}
          </QuestionCard>
        );

      case QUESTION_TYPES.LIKERT_SCALE:
        return (
          <QuestionCard
            key={qid}
            questionNumber={questionNumber}
            questionText={rawQuestionText}
            description={step.description}
            settings={settings}
            isRequired={isRequired}
            hasError={hasError}
          >
            {step.choices && step.choices.length > 0 ? (
              <Box sx={{
                display: "flex",
                gap: 1,
                justifyContent: "space-between",
                flexWrap: "wrap",
                mt: 1
              }}>
                {step.choices
                  .sort((a, b) => (a.index || 0) - (b.index || 0)) // Sort by index for consistent ordering
                  .map((choice) => (
                    <Box
                      key={choice.value || choice.text}
                      sx={{
                        textAlign: "center",
                        minWidth: { xs: "70px", sm: "120px" },
                        flex: "1 1 auto",
                        p: 1,
                        borderRadius: "8px",
                        transition: "all 0.2s ease-in-out",
                        cursor: isInteractive ? "pointer" : "default",
                        '&:hover': {
                          backgroundColor: isInteractive ? 'rgba(0, 0, 0, 0.04)' : 'transparent',
                        }
                      }}
                      onClick={() => isInteractive && handleAnswerChange(qid, choice.value || choice.text)}
                    >
                      <Radio
                        disabled={!isInteractive}
                        checked={answers[qid] === (choice.value || choice.text)}
                        name={`likert-${qid}`}
                        value={choice.value || choice.text}
                        onChange={(e) => isInteractive && handleAnswerChange(qid, e.target.value)}
                        sx={{
                          color: rgbaToCss(settings.choiceFontColor || settings.fontColour),
                          '&.Mui-disabled': {
                            color: rgbaToCss(settings.choiceFontColor || settings.fontColour),
                            opacity: 0.6
                          },
                          transform: "scale(1.2)",
                          mb: 0.5,
                        }}
                      />
                      <Typography
                        sx={{
                          color: rgbaToCss(settings.choiceFontColor || settings.fontColour),
                          fontSize: "0.75rem",
                          fontWeight: answers[qid] === (choice.value || choice.text) ? 600 : 400,
                          lineHeight: 1.2,
                          mt: 0.5
                        }}
                      >
                        {choice.text}
                      </Typography>
                    </Box>
                  ))}
              </Box>
            ) : (
              <Typography sx={{ color: rgbaToCss(settings.fontColour), fontStyle: "italic", textAlign: "center", py: 2 }}>
                No scale options available for this question.
              </Typography>
            )}
          </QuestionCard>
        );

      default:
        return null;
    }
  }, [pages, currentPage, answers, settings, mode, handleAnswerChange, validationErrors, surveyId, token]);

  // Show success page if submitted in respond mode
  if (isSubmitted && mode === "respond") {
    return <SurveyCompletion settings={settings} />;
  }

  return (
    <>
      <Box sx={getBackgroundStyle()}>
        {/* Background brightness overlay - only for image mode */}
        {settings.backgroundMode === "image" && settings.backgroundImage && (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0, 0, 0, 1)",
              opacity: backgroundDimming(settings),
              zIndex: 1,
              pointerEvents: "none",
            }}
          />
        )}

        {/* Logo */}
        {settings.logo && (
          <Box
            sx={{
              position: "absolute",
              top: 16,
              ...(settings.logoAlignment?.toLowerCase() === "center"
                ? { left: "50%", transform: "translateX(-50%)" }
                : { [settings.logoAlignment?.toLowerCase() || "left"]: 16 }),
              zIndex: 15,
            }}
          >
            <img
              src={settings.logo}
              alt="Logo"
              style={{
                maxWidth: "clamp(80px, 20vw, 150px)",
                maxHeight: "clamp(40px, 10vw, 75px)",
                objectFit: "contain",
              }}
            />
          </Box>
        )}

        {/* Main Content */}
        <Box
          ref={scrollContainerRef}
          sx={{
            position: "relative",
            zIndex: 10,
            minHeight: "100vh",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            paddingTop: settings.logo ? { xs: "90px", sm: "120px" } : { xs: "32px", sm: "80px" },
            paddingBottom: { xs: "24px", sm: "40px" },
            paddingX: { xs: 1, sm: 3 },
            overflowY: "auto",
          }}
        >
          {/* Survey Title and Description */}
          <Box sx={{ textAlign: "center", maxWidth: "800px", width: "100%", mb: { xs: 2, sm: 4 } }}>
            <Typography
              variant="h4"
              sx={{
                color: rgbaToCss(settings.surveyTitleColor || settings.fontColour),
                mb: 2,
                fontWeight: 600,
                fontSize: { xs: "1.4rem", sm: "2.125rem" },
              }}
            >
              {settings.title || intl.formatMessage({ id: "survey_builder.default_survey_title", defaultMessage: "Assessment Title" })}
            </Typography>
            {settings.description && (
              <Typography
                variant="h6"
                sx={{
                  color: rgbaToCss(settings.surveyDescriptionColor || settings.fontColour),
                  opacity: 0.8,
                  lineHeight: 1.5,
                  fontSize: { xs: "0.95rem", sm: "1.25rem" },
                }}
              >
                {settings.description}
              </Typography>
            )}
          </Box>

          {/* Progress Bar */}
          {settings.showProgressBar && totalPages > 1 && (
            <Box sx={{ mb: 3, width: "100%", maxWidth: "600px" }}>
              <Box sx={{ width: "100%", backgroundColor: "rgba(255,255,255,0.3)", borderRadius: "4px", height: "8px" }}>
                <Box
                  sx={{
                    width: `${((currentPage + 1) / totalPages) * 100}%`,
                    backgroundColor: rgbaToCss(settings.fontColour),
                    height: "100%",
                    borderRadius: "4px",
                    transition: "width 0.3s ease",
                  }}
                />
              </Box>
              <Typography
                variant="body2"
                sx={{
                  color: rgbaToCss(settings.fontColour),
                  mt: 1,
                  textAlign: "center",
                  opacity: 0.8,
                }}
              >
                Page {currentPage + 1} of {totalPages}
              </Typography>
            </Box>
          )}

          {/* Required fields legend – only in respond mode when the current page has required questions */}
          {mode === "respond" && pages[currentPage]?.some(s => s.is_required || s.isMandatory) && (
            <Box sx={{ width: "100%", maxWidth: "800px", mb: 1, textAlign: "right" }}>
              <Typography
                component="span"
                sx={{ fontSize: "0.8rem", color: rgbaToCss(settings.fontColour), opacity: 0.75 }}
              >
                <Typography
                  component="span"
                  sx={{ color: "#d32f2f", fontWeight: 700, fontSize: "1.1rem", mr: 0.5 }}
                >
                  *
                </Typography>
                {intl.formatMessage({ id: "survey.required_field_legend", defaultMessage: "Indicates required fields" })}
              </Typography>
            </Box>
          )}

          {/* Questions */}
          {pages[currentPage] && pages[currentPage].length > 0 ? (
            <Box sx={{ width: "100%", maxWidth: "800px" }}>
              {pages[currentPage].map((step, index) => renderQuestion(step, index))}
            </Box>
          ) : (
            <Typography sx={{
              color: rgbaToCss(settings.fontColour),
              textAlign: "center",
              py: 4,
              opacity: 0.7,
            }}>
              {intl.formatMessage({ id: "survey_builder.no_questions_on_page", defaultMessage: "No questions on this page." })}
            </Typography>
          )}

          {/* Validation error message */}
          {validationErrors.size > 0 && (
            <Alert
              severity="error"
              onClose={() => setValidationErrors(new Set())}
              sx={{ mt: 2, maxWidth: "800px", width: "100%" }}
            >
              {intl.formatMessage({ id: "survey.required_questions_message", defaultMessage: "Please answer all required questions before submitting." })}
            </Alert>
          )}

          {/* Navigation */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              width: "100%",
              maxWidth: "800px",
              mt: { xs: 2, sm: 4 },
              gap: { xs: 1, sm: 2 },
            }}
          >
            <Button
              onClick={handlePrevious}
              disabled={currentPage === 0 || isSaving}
              variant="contained"
              startIcon={isSaving ? <CircularProgress size={16} /> : <NavigateBefore />}
              size="medium"
              sx={{
                backgroundColor: settings.backButtonBackgroundColor || "rgba(255,255,255,0.9)",
                color: settings.backButtonFontColor || "rgba(0,0,0,0.8)",
                fontSize: { xs: "0.8rem", sm: "0.875rem" },
                px: { xs: 1.5, sm: 2 },
                minWidth: { xs: "80px", sm: "unset" },
                '&:hover': {
                  backgroundColor: settings.backButtonBackgroundColor ? `${settings.backButtonBackgroundColor}dd` : "rgba(255,255,255,1)",
                  opacity: 0.95,
                },
                '&:disabled': {
                  backgroundColor: settings.backButtonBackgroundColor ? `${settings.backButtonBackgroundColor}80` : "rgba(200,200,200,0.6)",
                  color: settings.backButtonFontColor ? `${settings.backButtonFontColor}80` : "rgba(0,0,0,0.4)",
                },
              }}
            >
              {settings.backButtonText || intl.formatMessage({ id: "survey_builder.previous", defaultMessage: "Previous" })}
            </Button>

            {currentPage === totalPages - 1 ? (
              <Button
                onClick={handleSubmit}
                disabled={isSubmitting}
                variant="contained"
                endIcon={isSubmitting ? <CircularProgress size={16} /> : <Send />}
                size="medium"
                sx={{
                  backgroundColor: settings.submitButtonBackgroundColor || "rgba(255,255,255,0.9)",
                  color: settings.submitButtonFontColor || "rgba(0,0,0,0.8)",
                  fontSize: { xs: "0.8rem", sm: "0.875rem" },
                  px: { xs: 1.5, sm: 2 },
                  minWidth: { xs: "90px", sm: "unset" },
                  '&:hover': {
                    backgroundColor: settings.submitButtonBackgroundColor ? `${settings.submitButtonBackgroundColor}dd` : "rgba(255,255,255,1)",
                    opacity: 0.95,
                  },
                  '&:disabled': {
                    backgroundColor: settings.submitButtonBackgroundColor ? `${settings.submitButtonBackgroundColor}80` : "rgba(200,200,200,0.6)",
                    color: settings.submitButtonFontColor ? `${settings.submitButtonFontColor}80` : "rgba(0,0,0,0.4)",
                  },
                }}
              >
                {isSubmitting ? intl.formatMessage({ id: "survey_builder.submitting", defaultMessage: "Submitting..." }) : (settings.submitButtonText || intl.formatMessage({ id: "survey_builder.submit", defaultMessage: "Submit" }))}
              </Button>
            ) : (
              <Button
                onClick={handleNext}
                disabled={isSaving}
                variant="contained"
                endIcon={isSaving ? <CircularProgress size={16} /> : <NavigateNext />}
                size="medium"
                sx={{
                  backgroundColor: settings.nextButtonBackgroundColor || "rgba(255,255,255,0.9)",
                  color: settings.nextButtonFontColor || "rgba(0,0,0,0.8)",
                  fontSize: { xs: "0.8rem", sm: "0.875rem" },
                  px: { xs: 1.5, sm: 2 },
                  minWidth: { xs: "80px", sm: "unset" },
                  '&:hover': {
                    backgroundColor: settings.nextButtonBackgroundColor ? `${settings.nextButtonBackgroundColor}dd` : "rgba(255,255,255,1)",
                    opacity: 0.95,
                  },
                }}
              >
                {settings.nextButtonText || intl.formatMessage({ id: "survey_builder.next", defaultMessage: "Next" })}
              </Button>
            )}
          </Box>

        </Box>
      </Box>

      {/* Submit confirmation dialog */}
      <Dialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        PaperProps={{ sx: { borderRadius: "12px", p: 1, minWidth: { xs: "90vw", sm: "400px" } } }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          {intl.formatMessage({ id: "survey.confirm_submit_title", defaultMessage: "Submit your response?" })}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {intl.formatMessage({
              id: "survey.confirm_submit_body",
              defaultMessage: "Once submitted you won't be able to change your answers. Are you sure you want to continue?",
            })}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button
            onClick={() => setConfirmOpen(false)}
            variant="outlined"
            disabled={isSubmitting}
          >
            {intl.formatMessage({ id: "survey.confirm_cancel", defaultMessage: "Go back" })}
          </Button>
          <Button
            onClick={handleConfirmSubmit}
            variant="contained"
            disabled={isSubmitting}
            endIcon={isSubmitting ? <CircularProgress size={16} /> : null}
          >
            {isSubmitting
              ? intl.formatMessage({ id: "survey_builder.submitting", defaultMessage: "Submitting..." })
              : intl.formatMessage({ id: "survey.confirm_submit_confirm", defaultMessage: "Submit" })}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

// Sub-components for better organization

function QuestionCard({ questionNumber, questionText, description, settings, children, isRequired, hasError }) {
  const intl = useIntl();

  return (
    <Card
      sx={{
        backgroundColor: questionCardBackground(settings),
        color: rgbaToCss(settings.fontColour),
        borderRadius: "12px",
        p: { xs: 2, sm: 3 },
        mb: 2,
        width: { xs: "90%", sm: "100%" },
        boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
        transition: "all 0.3s ease",
        border: hasError ? "2px solid #d32f2f" : "none",
        '&:hover': {
          boxShadow: "0 6px 25px rgba(0,0,0,0.15)",
        }
      }}
    >
      <CardContent sx={{ p: 0 }}>
        <Box sx={{ display: "flex", alignItems: "baseline", mb: 2 }}>
          {settings.showQuestionNumber && questionNumber && (
            <Typography
              variant="h6"
              sx={{
                mr: 1.5,
                color: rgbaToCss(settings.questionFontColor || settings.fontColour),
                fontWeight: 600,
                fontSize: "1.1rem"
              }}
            >
              {questionNumber}.
            </Typography>
          )}
          <Box sx={{ flexGrow: 1 }}>
            <Box>
              <Typography
                variant="h6"
                className="quill-content"
                dangerouslySetInnerHTML={{
                  __html: isRequired
                    ? questionText.replace(/(<\/\s*(?:p|div|h[1-6]|li|span)>)\s*$/i,
                      ' <span style="color:#d32f2f;font-weight:700;white-space:nowrap">*</span>$1')
                    : questionText,
                }}
                sx={{
                  color: rgbaToCss(settings.questionFontColor || settings.fontColour),
                  fontWeight: 600,
                  fontSize: { xs: "1rem", sm: "1.1rem" },
                  lineHeight: 1.4,
                }}
              />
            </Box>
            {description && description !== "<p><br></p>" && (
              <Typography
                variant="body2"
                className="quill-content"
                dangerouslySetInnerHTML={{ __html: description }}
                sx={{
                  color: rgbaToCss(settings.questionFontColor || settings.fontColour),
                  mt: 1,
                  opacity: 0.8,
                  fontSize: "0.9rem"
                }}
              />
            )}
          </Box>
        </Box>
        {children}
      </CardContent>
    </Card>
  );
}

function ContentCard({ questionNumber, contentText, description, settings }) {

  return (
    <Card
      sx={{
        backgroundColor: questionCardBackground(settings),
        color: rgbaToCss(settings.fontColour),
        borderRadius: "12px",
        p: { xs: 2, sm: 3 },
        mb: 2,
        width: { xs: "90%", sm: "100%" },
        boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
      }}
    >
      <CardContent sx={{ p: 0 }}>
        <Box sx={{ display: "flex", alignItems: "baseline", mb: description && description !== "<p><br></p>" ? 1 : 0 }}>
          {settings.showQuestionNumber && questionNumber && (
            <Typography
              variant="h6"
              sx={{
                mr: 1.5,
                color: rgbaToCss(settings.fontColour),
                fontWeight: 600,
                fontSize: "1.1rem",
                opacity: 0.7
              }}
            >
              {questionNumber}.
            </Typography>
          )}
          <Box sx={{ flexGrow: 1 }}>
            <Typography
              variant="h6"
              className="quill-content"
              dangerouslySetInnerHTML={{ __html: contentText }}
              sx={{
                color: rgbaToCss(settings.fontColour),
                fontWeight: 600,
                fontSize: { xs: "1rem", sm: "1.1rem" },
                lineHeight: 1.4
              }}
            />
          </Box>
        </Box>
        {description && description !== "<p><br></p>" && (
          <Typography
            variant="body1"
            className="quill-content"
            dangerouslySetInnerHTML={{ __html: description }}
            sx={{
              color: rgbaToCss(settings.fontColour),
              fontSize: "1rem",
              lineHeight: 1.5,
              mt: 1
            }}
          />
        )}
      </CardContent>
    </Card>
  );
}

function ChoiceOption({ choice, isSelected, onSelect, isInteractive, settings, isMultiChoice = false }) {
  const ControlComponent = isMultiChoice ? Checkbox : Radio;

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        p: 1.5,
        borderRadius: "8px",
        transition: "all 0.2s ease-in-out",
        cursor: isInteractive ? "pointer" : "default",
        border: isSelected ? `2px solid ${rgbaToCss(settings.choiceFontColor || settings.fontColour)}` : "2px solid transparent",
        backgroundColor: isSelected
          ? `rgba(${(settings.choiceFontColor || settings.fontColour) === "#000000" ? "0,0,0" : "25,118,210"}, 0.08)`
          : "transparent",
        '&:hover': {
          backgroundColor: isInteractive ? 'rgba(0, 0, 0, 0.04)' : 'transparent',
          borderColor: isInteractive ? rgbaToCss(settings.choiceFontColor || settings.fontColour) : "transparent",
        },
      }}
      onClick={onSelect}
    >
      <ControlComponent
        disabled={!isInteractive}
        checked={isSelected}
        sx={{
          color: rgbaToCss(settings.choiceFontColor || settings.fontColour),
          '&.Mui-disabled': {
            color: rgbaToCss(settings.choiceFontColor || settings.fontColour),
            opacity: 0.6
          },
          transform: "scale(1.3)",
          mr: 1.5,
        }}
      />
      <Typography
        component="span"
        sx={{
          color: rgbaToCss(settings.choiceFontColor || settings.fontColour),
          fontWeight: isSelected ? 600 : 400,
          fontSize: "1rem"
        }}
      >
        {choice.text || "Choice (Please add text)"}
      </Typography>
    </Box>
  );
}

export default SurveyPreview;
